import {Formik, FormikProps} from 'formik';
import {forEach, map} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {supportApi} from 'api/support';
import {ConfirmModal, PredefinedNotifierType, SelectFormik, TextAreaFormik} from 'components';
import {SupportCategory} from 'enums';
import {useApiData, usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, ContactSupportInfo} from 'types';
import {apiUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.CONTACT_SUPPORT_MODAL;
const TEXT_AREA_MAX_LENGTH = 2000;

const CATEGORY_NAME_T_KEY = {
    [SupportCategory.RequestFeature]: T_KEYS.CATEGORIES.REQUEST_FEATURE,
    [SupportCategory.ReportIssue]: T_KEYS.CATEGORIES.REPORT_ISSUE,
    [SupportCategory.PaymentSubscription]: T_KEYS.CATEGORIES.PAYMENT_SUBSCRIPTION,
    [SupportCategory.Other]: T_KEYS.CATEGORIES.OTHER
};

const FIELDS = {
    CATEGORY: 'category',
    DESCRIPTION: 'description'
};

type FormTemplateProps = {
    open: boolean;
    error?: ApiError;
    onClose: (confirm: boolean, data: ContactSupportInfo) => void;
} & FormikProps<ContactSupportInfo>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    open, values, error, dirty, resetForm, onClose, setFieldError, submitForm
}) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);

    const categoryOptions = useMemo(() =>
        map(CATEGORY_NAME_T_KEY, (value, key) => ({id: key, name: t(value || T_KEYS.CATEGORIES.OTHER)})),
    [t]);

    const okDisabled = useMemo(() => !dirty || !values.category || !values.description, [dirty, values.category, values.description]);

    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false, null);
        }
    }, [submitForm, onClose]);

    useEffect(() => {
        if (!open && prevOpen) {
            resetForm();
        }
    }, [open, prevOpen, resetForm]);

    useEffect(() => {
        const formErrors = apiUtils.getFieldsValidationErrorMessages(error, t, map(FIELDS, (field) => ({field})));
        forEach(formErrors, (value, key) => value && setFieldError(key, value));
    }, [error, t, setFieldError]);

    return (
        <ConfirmModal
            fullWidth
            maxWidth='sm'
            open={open}
            title={t(T_KEYS.TITLE)}
            okDisabled={okDisabled}
            okText={t(T_KEYS.BUTTONS.SEND)}
            onClose={onModalClose}
        >
            <SelectFormik
                name={FIELDS.CATEGORY}
                placeholder={t(T_KEYS.FIELDS.CATEGORY)}
                options={categoryOptions}
                required
                valueAs='id'
            />
            <TextAreaFormik
                className='tw-mt-4'
                name={FIELDS.DESCRIPTION}
                required
                rows={5}
                maxLength={TEXT_AREA_MAX_LENGTH}
                label={t(T_KEYS.FIELDS.DESCRIPTION)}
            />
        </ConfirmModal>
    );
};

type Props = {
    open: boolean;
    onClose: (open: boolean) => void;
};

export const ContactSupportModal: React.FC<Props> = ({open, onClose}) => {
    const initialValues = useMemo(() => ({
        category: '',
        description: ''
    }), []);

    const onSuccessSupportRequest = useCallback(() => {
        onClose(false);
    }, [onClose]);

    const {send: sendSupportRequest, error: contactSupportError} = useApiData(supportApi.contactSupport,
        {onSuccess: onSuccessSupportRequest, notificationType: PredefinedNotifierType.Send});

    const handleModalClose = useCallback((confirm: boolean, data: ContactSupportInfo) => {
        if (confirm) {
            sendSupportRequest(data);
        } else {
            onClose(false);
        }
    }, [onClose, sendSupportRequest]);

    const handleSubmit = useCallback((values: ContactSupportInfo) => {
        handleModalClose(true, values);
    }, [handleModalClose]);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    open={open}
                    error={contactSupportError}
                    onClose={handleModalClose}
                />
            )}
        </Formik>
    );
};